import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ShieldAlert, AlertTriangle, Clock } from "lucide-react";
import SkeletonGrid from "@/components/SkeletonGrid";
import { Jargon } from "@/components/admin/JargonTerm";
import DashboardGridWrapper from "@/components/dashboard/DashboardGridWrapper";
import PageHeader from "@/components/dashboard/PageHeader";

/**
 * AdminLayer11Audit (Behavioural Guardrails, /admin/compliance/layer11-audit)
 * — compliance dashboard showing every Layer 11 opt-out toggle and
 * kill-switch event across the tenant. Admin-only.
 *
 * UI Architecture Parity (2026-09-05 directive): shares the Sidebar/AppLayout
 * shell (no own full-page wrapper) and uses DashboardGridWrapper so the
 * summary tiles + events table can be dragged/resized like every other
 * Super Admin Dashboard module.
 */
export default function AdminLayer11Audit() {
    const [data, setData]       = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter]   = useState("all"); // all | opt_out | kill_switch

    useEffect(() => {
        api.get("/admin/compliance/layer11-audit?limit=500")
            .then((r) => { setData(r.data); setLoading(false); })
            .catch(() => setLoading(false));
    }, []);

    const events = (data?.events || []).filter((e) => {
        if (filter === "opt_out")    return e.action === "layer11_opt_out_toggle";
        if (filter === "kill_switch") return e.action === "layer11_kill_switch";
        return true;
    });

    const widgets = [
        {
            id: "guardrails-summary", title: "Summary", category: "Overview",
            layout: { x: 0, y: 0, w: 12, h: 3 }, minW: 6, minH: 2, noPadding: true,
            render: () => (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-3" data-testid="guardrails-summary-tiles">
                    <Tile label={<><Jargon term="kill_switch" /> ON</>} value={data?.kill_switch_on ?? "—"}
                          tone="rose" testId="tile-kill-switch-on" />
                    <Tile label="Active users" value={data?.active_users ?? "—"} testId="tile-active-users" />
                    <Tile label="Events (500 latest)" value={events.length} testId="tile-events-count" />
                    <Tile label="Rule opt-outs"
                          value={(data?.events || []).filter((e) => e.action === "layer11_opt_out_toggle" && e.opt_out).length}
                          testId="tile-opt-outs" />
                </div>
            ),
        },
        {
            id: "guardrails-events", title: "Events", category: "Audit",
            layout: { x: 0, y: 3, w: 12, h: 14 }, minW: 6, minH: 6,
            render: () => (
                <div className="space-y-3" data-testid="guardrails-events-widget">
                    <div className="flex gap-2" data-testid="audit-filter-row">
                        {[
                            { k: "all",         label: "All events" },
                            { k: "opt_out",     label: "Rule opt-outs" },
                            { k: "kill_switch", label: <Jargon term="kill_switch" /> },
                        ].map((f) => (
                            <button
                                key={f.k}
                                onClick={() => setFilter(f.k)}
                                className={`text-ui-base px-2.5 py-1 rounded border ${filter === f.k
                                    ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-muted/40"}`}
                                data-testid={`audit-filter-${f.k}`}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>

                    <div className="border border-border bg-card rounded-xl overflow-hidden">
                        {loading ? (
                            <div className="p-4"><SkeletonGrid variant="rows" count={6} /></div>
                        ) : events.length === 0 ? (
                            <div className="p-8 text-center text-ui-base text-muted-foreground">
                                No compliance events yet. Traders haven't touched their guardrails.
                            </div>
                        ) : (
                            <table className="w-full text-ui-base">
                                <thead className="text-caption uppercase tracking-widest text-muted-foreground bg-secondary/30">
                                    <tr>
                                        <th className="text-left px-3 py-2">When</th>
                                        <th className="text-left px-3 py-2">Trader</th>
                                        <th className="text-left px-3 py-2">Tier</th>
                                        <th className="text-left px-3 py-2">Action</th>
                                        <th className="text-left px-3 py-2">Detail</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {events.map((e) => (
                                        <tr key={e.id} className="border-t border-border/60"
                                            data-testid={`audit-row-${e.id}`}>
                                            <td className="px-3 py-2 text-caption text-muted-foreground font-mono">
                                                <Clock className="w-3 h-3 inline mr-1" />
                                                {new Date(e.ts).toLocaleString()}
                                            </td>
                                            <td className="px-3 py-2">
                                                <div className="font-medium">{e.user_name}</div>
                                                <div className="text-caption text-muted-foreground">{e.user_email}</div>
                                            </td>
                                            <td className="px-3 py-2 text-caption uppercase tracking-widest text-muted-foreground">
                                                {e.user_tier || "—"}
                                            </td>
                                            <td className="px-3 py-2">
                                                {e.action === "layer11_kill_switch" ? (
                                                    <span className="inline-flex items-center gap-1 text-rose-500 font-semibold">
                                                        <AlertTriangle className="w-3.5 h-3.5" /> Kill-switch
                                                    </span>
                                                ) : (
                                                    <span className={`inline-flex items-center gap-1 ${e.opt_out ? "text-amber-500" : "text-emerald-500"}`}>
                                                        {e.opt_out ? "Opted out" : "Re-enabled"}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-caption font-mono text-muted-foreground">
                                                {e.rule_code || (e.language ? `lang=${e.language}` : "—")}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            ),
        },
    ];

    return (
        <div className="space-y-4" data-testid="admin-layer11-audit-page">
            <div>
                <PageHeader
                    icon={ShieldAlert}
                    eyebrow="Modules"
                    title="Behavioural Guardrails"
                    subtitle="Every rule toggle and kill-switch event. Institutional risk managers should escalate any trader appearing here more than three times a week."
                    testId="admin-layer11-audit-header"
                />
            </div>

            <DashboardGridWrapper role="SUPER_ADMIN" viewId="admin-layer11-audit" widgets={widgets} />
        </div>
    );
}

function Tile({ label, value, tone, testId }) {
    const cls = tone === "rose" ? "text-rose-500" : "text-foreground";
    return (
        <div className="border border-border bg-card rounded-lg p-3" data-testid={testId}>
            <div className="text-caption uppercase tracking-widest text-muted-foreground">{label}</div>
            <div className={`font-heading text-h3 font-mono proportional-nums ${cls}`}>{value}</div>
        </div>
    );
}
